import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import SiteFooter from "@/components/SiteFooter";

const UPDATED = "24 septembre 2026";
const DEFAULT_HOST = "Site hébergé par Vercel Inc. (vercel.com). Serveur de l'application hébergé par Render Services, Inc. (render.com).";

function useLegal() {
  const [legal, setLegal] = useState(null);
  useEffect(() => {
    axios.get(`${API}/legal`).then((r) => setLegal(r.data)).catch(() => setLegal({}));
  }, []);
  return legal;
}

function sellerLines(legal) {
  return [
    legal.legal_name,
    legal.legal_status,
    legal.legal_address,
    legal.legal_siret && `SIRET : ${legal.legal_siret}`,
    legal.legal_email && `E-mail : ${legal.legal_email}`,
    legal.legal_phone && `Téléphone : ${legal.legal_phone}`,
  ].filter(Boolean).join("\n");
}

function LegalLayout({ title, legal, sections, testid }) {
  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <Navbar />
      <main className="pt-24 pb-16 max-w-4xl mx-auto px-6" data-testid={testid}>
        <h1 className="text-3xl font-bold mb-2">{title}</h1>
        <p className="text-white/40 text-sm mb-8">Dernière mise à jour : {UPDATED}</p>
        {!legal ? (
          <div className="flex justify-center py-12"><div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full" /></div>
        ) : (
          <div className="space-y-8">
            {sections.filter((s) => s && s.body).map((s) => (
              <section key={s.title}>
                <h2 className="text-xl font-semibold text-white mb-3">{s.title}</h2>
                {typeof s.body === "string" ? <p className="text-white/60 leading-relaxed whitespace-pre-line">{s.body}</p> : s.body}
              </section>
            ))}
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}

export function LegalNotice() {
  const legal = useLegal();
  const l = legal || {};
  const sections = [
    { title: "Éditeur du site", body: sellerLines(l) || "VantaPulse" },
    { title: "Directeur de la publication", body: l.legal_director || l.legal_name || "" },
    { title: "Hébergement", body: l.legal_host || DEFAULT_HOST },
    {
      title: "Propriété intellectuelle",
      body: "Le site, la marque VantaPulse, les textes, visuels et le code des produits vendus sont protégés par le droit d'auteur. Toute reproduction ou redistribution sans autorisation écrite est interdite.",
    },
    {
      title: "Données personnelles",
      body: <p className="text-white/60 leading-relaxed">Le traitement de vos données est décrit dans la <Link to="/politique-confidentialite" className="text-blue-400 underline">politique de confidentialité</Link>. Vous pouvez exercer vos droits (accès, rectification, suppression) en nous écrivant{l.legal_email ? ` à ${l.legal_email}` : " depuis la page Support"}.</p>,
    },
    {
      title: "Contact",
      body: l.legal_email ? `Pour toute question : ${l.legal_email}` : "Pour toute question : page Support du site.",
    },
  ];
  return <LegalLayout title="Mentions légales" legal={legal} sections={sections} testid="legal-notice" />;
}

export function SalesTerms() {
  const legal = useLegal();
  const l = legal || {};
  const vat = l.legal_vat_mention ? ` ${l.legal_vat_mention}.` : "";
  const sections = [
    { title: "1. Vendeur", body: sellerLines(l) || "VantaPulse" },
    {
      title: "2. Objet",
      body: "Les présentes conditions générales de vente (CGV) s'appliquent à toute commande passée sur le site : produits numériques (scripts pour serveurs FiveM livrés avec une clé de licence, bots Discord hébergés), sous forme d'abonnement ou de licence à vie. Passer commande vaut acceptation des CGV en vigueur au jour de la commande, ainsi que des conditions d'utilisation du site.",
    },
    {
      title: "3. Produits",
      body: "Les caractéristiques de chaque produit sont décrites sur sa page. Un script est fourni avec une clé de licence valable pour un seul serveur à la fois ; la clé peut être libérée depuis le tableau de bord pour changer de serveur. Les mises à jour publiées pendant la durée de la licence sont incluses.",
    },
    {
      title: "4. Prix",
      body: `Les prix sont indiqués en euros, toutes taxes comprises.${vat} Le prix applicable est celui affiché au moment de la commande. Un code promo ne s'applique que dans ses conditions (produit, durée de validité, nombre d'utilisations, y compris par client).`,
    },
    {
      title: "5. Commande et paiement",
      body: "La commande se fait depuis un compte client. Le paiement par carte bancaire est traité par Stripe ; nous ne conservons aucune donnée bancaire. Un abonnement avec renouvellement automatique est prélevé chaque mois jusqu'à sa résiliation, possible à tout moment depuis le tableau de bord ; la période déjà payée reste acquise.",
    },
    {
      title: "6. Livraison",
      body: "Dès la confirmation du paiement, le produit est disponible dans le tableau de bord : clé de licence et fichier à télécharger pour un script, activation pour un bot. Un e-mail de confirmation est envoyé et une facture (ou un reçu) est disponible dans le tableau de bord.",
    },
    {
      title: "7. Droit de rétractation",
      body: "Scripts et contenus téléchargeables : ils sont fournis immédiatement. Au moment de la commande, vous demandez expressément cet accès immédiat et reconnaissez perdre votre droit de rétractation (article L221-28 13° du Code de la consommation). Aucune rétractation n'est donc possible une fois la commande validée.\nBots Discord (services) : vous disposez de 14 jours pour vous rétracter. Si vous avez demandé que le service commence avant la fin de ce délai, le montant correspondant au service déjà fourni reste dû.",
    },
    {
      title: "8. Essai gratuit",
      body: "Certains produits peuvent être essayés gratuitement pendant quelques jours, une seule fois par client et par serveur. À la fin de l'essai, la clé cesse de fonctionner ; en cas d'achat, la clé d'essai devient celle du produit acheté.",
    },
    {
      title: "9. Passage à un pack",
      body: "Un client qui possède à vie un ou plusieurs produits d'un pack peut passer au pack à vie en payant la différence : chaque produit possédé est déduit à hauteur de sa part dans le prix du pack. Les licences des produits remplacés sont désactivées et la clé déjà utilisée par le serveur devient celle du pack.",
    },
    {
      title: "10. Licence d'utilisation",
      body: "L'achat donne un droit d'utilisation personnel, non exclusif et non transférable, pour la durée de la formule choisie (« à vie » : tant que le produit est proposé et maintenu). Sont interdits : la revente, le partage ou la publication du code, du fichier ou de la clé, et le contournement de la vérification de licence. En cas de violation, la licence peut être désactivée sans remboursement.",
    },
    {
      title: "11. Garanties",
      body: "Vous bénéficiez de la garantie légale de conformité des contenus et services numériques (articles L224-25-12 et suivants du Code de la consommation). En cas de problème, contactez le support : si le produit ne peut pas être rendu conforme, vous pouvez obtenir une réduction du prix ou un remboursement.",
    },
    {
      title: "12. Remboursements",
      body: <p className="text-white/60 leading-relaxed">Les cas de remboursement sont détaillés dans la <Link to="/remboursement" className="text-blue-400 underline">politique de remboursement</Link>.</p>,
    },
    {
      title: "13. Médiation et litiges",
      body: "En cas de litige, contactez d'abord le support pour trouver une solution amiable. Vous pouvez ensuite recourir gratuitement à un médiateur de la consommation (article L612-1 du Code de la consommation) : ses coordonnées figurent dans les dispositions complémentaires ci-dessous ou vous sont communiquées sur simple demande. Les présentes CGV sont soumises au droit français.",
    },
    { title: "14. Dispositions complémentaires", body: l.cgv_extra || "" },
  ];
  return <LegalLayout title="Conditions générales de vente" legal={legal} sections={sections} testid="sales-terms" />;
}

const DEFAULT_REFUND = [
  {
    title: "Scripts et produits téléchargeables",
    body: "Ils sont livrés immédiatement et vous renoncez au droit de rétractation en commandant (voir les CGV). Ils sont en revanche remboursés s'ils ne fonctionnent pas comme décrit et que le support ne parvient pas à corriger le problème. Profitez de l'essai gratuit, quand il est proposé, pour vérifier la compatibilité avec votre serveur avant d'acheter.",
  },
  {
    title: "Abonnements",
    body: "Vous pouvez arrêter le renouvellement automatique à tout moment depuis le tableau de bord : le produit reste actif jusqu'à la fin de la période payée. La période en cours n'est pas remboursée, sauf dysfonctionnement non résolu.",
  },
  {
    title: "Bots Discord",
    body: "Vous pouvez vous rétracter dans les 14 jours suivant l'achat. Si le bot a déjà été activé à votre demande, le remboursement est calculé au prorata des jours restants.",
  },
  {
    title: "Paiement en double ou erreur de commande",
    body: "Un paiement en double ou une erreur manifeste est toujours remboursé.",
  },
];

export function RefundPolicy() {
  const legal = useLegal();
  const l = legal || {};
  const sections = [
    ...(l.refund_policy ? [{ title: "Notre politique", body: l.refund_policy }] : DEFAULT_REFUND),
    {
      title: "Comment demander un remboursement",
      body: <p className="text-white/60 leading-relaxed">Ouvrez un ticket depuis la page <Link to="/support" className="text-blue-400 underline">Support</Link> en choisissant le produit concerné{l.legal_email ? `, ou écrivez à ${l.legal_email}` : ""}. Indiquez la date de la commande et le problème rencontré. Un remboursement accordé est versé sous 14 jours sur la carte utilisée pour payer ; la licence correspondante est alors désactivée.</p>,
    },
  ];
  return <LegalLayout title="Politique de remboursement" legal={legal} sections={sections} testid="refund-policy" />;
}
